import { Environment } from '../enums/Environment.js';

/** Default environment values for non-Node runtimes (e.g. frontend apps). */
const defaults: { env: Record<string, string> } = {
  env: {
    ENV_MODE: Environment.Development,
    CONTAINERIZED: 'false'
  }
};

/**
 * Reads Node process env when available.
 *
 * @returns Process.env or an empty object.
 */
function ReadProcessEnv(): Record<string, string> {
  try {
    if (typeof process !== 'undefined' && process.env && typeof process.env === 'object') {
      return process.env as Record<string, string>;
    }
  } catch {
    // Non-Node runtime; ignore.
  }
  return {};
}

/**
 * Reads Vite-injected env when available (browser builds).
 *
 * @returns Import.meta.env or undefined.
 */
function ReadViteEnv(): Record<string, string> | undefined {
  try {
    const viteEnv = (import.meta as unknown as { env?: Record<string, string> }).env;
    if (viteEnv && typeof viteEnv === 'object') return viteEnv;
  } catch {
    // Non-Vite runtime; ignore.
  }
  return undefined;
}

/** Environment values from Node process env, when available. */
const processEnv = ReadProcessEnv();

/** Environment values from Vite's import.meta.env, when available. */
const viteEnv = ReadViteEnv();

/** Process-like env object used across services and frontend apps. */
const env: Record<string, string> = { ...defaults.env, ...processEnv };

if (viteEnv) {
  Object.assign(env, viteEnv);
}

// Resolve ENV_MODE across Node/Vite conventions. Node services set ENV_MODE directly; browser bundles get it via VITE_ENV_MODE (vite only
// exposes VITE_*-prefixed env vars). There is deliberately NO inference from vite's build MODE: vite stamps MODE='production' on EVERY
// production bundle build regardless of the target environment, so inferring an environment from it silently pointed any build missing the
// explicit flag at Production - the dangerous direction. An unresolved ENV_MODE always keeps the Development default above (safe: localhost
// URLs, visibly wrong if misdeployed); Test/Production bundles receive their value explicitly (--build-arg ENV_MODE -> VITE_ENV_MODE in the
// assets dockerfile).
if (processEnv.ENV_MODE) {
  env.ENV_MODE = processEnv.ENV_MODE;
} else if (viteEnv?.ENV_MODE) {
  env.ENV_MODE = viteEnv.ENV_MODE;
} else if (viteEnv?.VITE_ENV_MODE) {
  env.ENV_MODE = viteEnv.VITE_ENV_MODE;
}

/** Process-like object for accessing environment variables. */
export { env as EnvProcess };

/** Flag indicating if the current environment is production. */
export const IsProduction = env.ENV_MODE === Environment.Production;
